
import { useCallback, useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { getKudos } from "../../services/wisdomServices/growthService";
import "./RecentKudosDialog.css";

const LIMIT_OPTIONS = [5, 10, 15, 25].map((value) => ({
  label: `${value} Kudos`,
  value,
}));


function KudosAvatar({ name, image }) {
  const [imageError, setImageError] = useState(false);

  const initials = String(name || "U")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  const showImage = Boolean(image?.trim()) && !imageError;

  return (
    <div className="recent-kudos-item-avatar">
      {showImage ? (
        <img
          src={image}
          alt={name || "User"}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setImageError(true)}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
}

function RecentKudosDialog({ visible, onHide, refreshKey = 0 }) {
  const [limit, setLimit] = useState(5);
  const [kudos, setKudos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const loadKudos = useCallback(async (signal) => {
    setLoading(true);
    setError(false);

    try {
      const response = await getKudos(limit);
      if (signal.aborted) return;

      const items = Array.isArray(response)
        ? response
        : response?.data?.data ??
          response?.data?.Details ??
          response?.data ??
          response?.Details ??
          [];

      setKudos(Array.isArray(items) ? items.slice(0, limit) : []);
    } catch (error) {
      if (signal.aborted) return;
      console.error("Failed to load Kudos:", error);
      setError(true);
      setKudos([]);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    if (!visible) return;

    const controller = new AbortController();
    loadKudos(controller.signal);

    return () => controller.abort();
  }, [visible, loadKudos, refreshKey, retryKey]);

  const formatDate = (value) => {
    if (!value) return null;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const header = (
    <div className="recent-kudos-header">
      <div className="recent-kudos-header-icon">
        <i className="pi pi-star-fill" />
      </div>
      <div>
        <h2>Recent Kudos</h2>
        <p>Celebrating the people who make a difference.</p>
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={header}
      modal
      dismissableMask
      draggable={true}
      resizable={false}
      closeOnEscape
      blockScroll
      className="recent-kudos-dialog"
      style={{ width: "min(680px, 95vw)" }}
      breakpoints={{ "640px": "95vw" }}
    >
      <div className="recent-kudos-container">
        <div className="recent-kudos-toolbar">
          <div className="recent-kudos-toolbar-label">
            <i className="pi pi-sparkles" />
            <span>Recognition highlights</span>
          </div>

          <div className="recent-kudos-limit">
            <span>Show</span>
            <Dropdown
              value={limit}
              options={LIMIT_OPTIONS}
              onChange={(e) => setLimit(e.value)}
              className="recent-kudos-dropdown"
              aria-label="Number of Kudos to display"
            />
          </div>
        </div>

        <div className="recent-kudos-list">
          {loading ? (
            <div className="recent-kudos-state">
              <i className="pi pi-spin pi-spinner recent-kudos-state-icon" />
              <h3>Loading Kudos...</h3>
              <p>Fetching the latest recognition highlights.</p>
            </div>
          ) : error ? (
            <div className="recent-kudos-state">
              <i className="pi pi-exclamation-circle recent-kudos-state-icon" />
              <h3>Something went wrong</h3>
              <p>We couldn't load the Kudos. Please try again.</p>
              <Button
                label="Try again"
                icon="pi pi-refresh"
                outlined
                onClick={() => setRetryKey((prev) => prev + 1)}
              />
            </div>
          ) : kudos.length === 0 ? (
            <div className="recent-kudos-state">
              <i className="pi pi-star recent-kudos-state-icon" />
              <h3>No Kudos yet</h3>
              <p>Recognition messages will appear here.</p>
            </div>
          ) : (
            kudos.map((item, index) => {
              const recipient = item.recipientName || "Unknown";
              const sender = item.createdByName || null;
              const message =
                item.message ?? item.description ?? item.content ?? "";
              const date = formatDate(item.createdAt);

              return (
                <div
                  key={item.id ?? index}
                  className="recent-kudos-item"
                >
                  <KudosAvatar
                    key={`${item.id}-${item.recipientImage ?? ""}`}
                    name={recipient}
                    image={item.recipientImage}
                  />

                  <div className="recent-kudos-item-content">
                    <div className="recent-kudos-item-top">
                      <div className="recent-kudos-person">
                        <strong>{recipient}</strong>
                        <span>Received a Kudos</span>
                      </div>

                      <i className="pi pi-star-fill recent-kudos-star" />
                    </div>

                    {message && (
                      <p className="recent-kudos-message">
                        {message}
                      </p>
                    )}

                    {(sender || date) && (
                      <div className="recent-kudos-item-footer">
                        {sender && <span>From {sender}</span>}
                        {date && <time>{date}</time>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {!loading && !error && kudos.length > 0 && (
          <div className="recent-kudos-footer">
            <span>
              Showing {kudos.length} Kudos
            </span>
          </div>
        )}
      </div>
    </Dialog>
  );
}

export default RecentKudosDialog;
